import { Box, Button, Checkbox, InputBase } from '@material-ui/core';
import { useCallback, useEffect, useMemo, useState, ChangeEvent, useRef } from 'react';

import { Accordion, TextField, Typography } from '@components';
import { content } from '@content';
import { Libraries } from '@routes';
import { clipboard, textTemplate, validate } from '@utils';

import { roundByRank } from '@utils/format/format';
// import { EditMode } from '../EditMode';
import { ImageContainerProps, ImageContainerState } from './ImageContainer.props';
import { useStyles } from './ImageContainer.styles';

/**
 ImageContainer component.
 @returns {JSX.Element}
 */

export const ImageContainer = ({
  chosenImage,
  uiType = 'primary',
  initialValues,
  isModuleChanged,
  isImageEditor = false,
  isDVEditor = false,
  dirty = false,
  panelTitleValue,
  setPanelTitleValue,
  setIsModuleChanged,
  onChange,
  setChosenLibrary,
  onDimensionsChange,
  onLinkChange,
  onTitleChange,
  onAltTextChange,
  onCloseImageEditor,
  onSave,
  onModuleClose,
  onModuleSave,
}: ImageContainerProps): JSX.Element => {
  const MAX_IMAGE_WIDTH = '600';
  const MAX_IMAGE_HEIGHT = '2000';

  const styles = useStyles();
  const [state, setState] = useState<ImageContainerState>(null);
  const [isSectionExpanded, setIsSectionExpanded] = useState<boolean[]>([true, false]);
  const [maintainProportions, setMaintainProportions] = useState(true);
  const [activeDimensionField, setActiveDimensionField] = useState<'width' | 'height' | undefined>('width');
  const [isEditTitleModeOn, setIsEditTitleModeOn] = useState(false);

  // const { width, height, name } = chosenImage;
  const { name } = chosenImage;

  const imageRatio = useMemo(() => {
    return roundByRank(Number(chosenImage.width) / Number(chosenImage.height), 10000);
  }, [chosenImage.width, chosenImage.height]);

  const didMount = useRef(false);

  useEffect(() => {
    if (initialValues && !isModuleChanged) {
      setState((prevState) => ({ ...prevState, ...initialValues }));
    }
  }, [initialValues, isModuleChanged]);

  // useEffect(() => {
  //   onChange(state);
  // }, [state, onChange]);

  useEffect(() => {
    if (didMount.current) {
      onChange(state);
    } else {
      didMount.current = true;
    }
  }, [state, onChange]);

  const handleSelectImage = useCallback(() => {
    setChosenLibrary(Libraries.IMAGES);
    clipboard.copy(state?.imageSrc ?? '');
  }, [state, setChosenLibrary]);

  const handleImageChange = useCallback(
    (value: string) => {
      setState((prevState) => ({ ...prevState, imageSrc: value }));
      setIsModuleChanged(true);
    },
    [setState, setIsModuleChanged],
  );

  const handleCheckChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setMaintainProportions(e.target.checked);
  };

  // Only update state here, do not call onDimensionsChange directly
  const handleDimensionsChange = useCallback(
    (param: 'width' | 'height', value: string) => {
      if (validate.isNumeric(value)) {
        setState((prevState) => {
          const newState = { ...prevState, [param]: value };
          if (param === 'height' && maintainProportions) {
            newState.width = roundByRank(Number(value) * imageRatio, 1).toString();
          } else if (param === 'width' && maintainProportions) {
            newState.height = roundByRank(Number(value) / imageRatio, 1).toString();
          }
          return newState;
        });
        setIsModuleChanged(true);
      }
      switch (param) {
        case 'width':
          setActiveDimensionField('width');
          break;
        case 'height':
          setActiveDimensionField('height');
          break;
      }
    },
    [setIsModuleChanged, maintainProportions, imageRatio],
  );

  // Call onDimensionsChange only after state changes, not during render
  useEffect(() => {
    if (!state) return;
    if (
      onDimensionsChange &&
      typeof state.width === 'string' &&
      typeof state.height === 'string' &&
      validate.isNumeric(state.width) &&
      validate.isNumeric(state.height)
    ) {
      onDimensionsChange([Number(state.width), Number(state.height)]);
    }
    // Only run when width/height change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state?.width, state?.height]);

  const handleTitleChange = useCallback(
    (value: string) => {
      if (onTitleChange) {
        onTitleChange(value);
      }

      setState((prevState) => ({ ...prevState, title: value }));
      setIsModuleChanged(true);
    },
    [onTitleChange, setIsModuleChanged],
  );

  const handleLinkChange = useCallback(
    (value: string) => {
      if (onLinkChange) {
        onLinkChange(value);
      }

      // clear the title if link is empty
      if (!value || value.trim() === '') {
        handleTitleChange('');
      }

      setState((prevState) => ({ ...prevState, link: value }));
      setIsModuleChanged(true);
    },
    [onLinkChange, handleTitleChange, setIsModuleChanged],
  );

  const handleAltTextChange = useCallback(
    (value: string) => {
      if (onAltTextChange) {
        onAltTextChange(value);
      }

      setState((prevState) => ({ ...prevState, altText: value }));
      setIsModuleChanged(true);
    },
    [onAltTextChange, setIsModuleChanged],
  );

  const handleAccordionChange = useCallback((index: number) => {
    setIsSectionExpanded((prevState) =>
      prevState.map((item, ind) => {
        if (ind === index) {
          return !item;
        }

        return false;
      }),
    );
  }, []);

  const getContent = () => {
    return (
      <>
        {!isDVEditor && (
          <Box className={styles.imageSection}>
            <Typography.SuperSmallCaption className={styles.caption}>{content.imagePath}</Typography.SuperSmallCaption>
            <Box className={styles.imageSrcWrapper}>
              <TextField
                className={styles.imageSrc}
                size="small"
                direction="vertical"
                placeholder={content.none}
                value={state?.imageSrc}
                onChange={handleImageChange}
                disabled={true}
              />
              <Button className={styles.selectBtn} variant="outlined" color="primary" onClick={handleSelectImage}>
                {content.copy}
              </Button>
            </Box>
          </Box>
        )}
        <Box className={styles.widthSection}>
          <Box className={styles.width}>
            <Typography.SuperSmallCaption className={styles.caption}>{content.width}</Typography.SuperSmallCaption>
            <TextField
              size="small"
              direction="vertical"
              placeholder={content.notSet}
              value={state?.width && state.width}
              maxValue={chosenImage.chosenModuleElemId ? MAX_IMAGE_WIDTH : 'auto'}
              type={'number'}
              onChange={(value) => handleDimensionsChange('width', value)}
            />
          </Box>
          <Box>
            <Typography.SuperSmallCaption className={styles.caption}>{content.height}</Typography.SuperSmallCaption>
            <TextField
              size="small"
              direction="vertical"
              placeholder={content.notSet}
              value={state?.height && state.height}
              maxValue={chosenImage.chosenModuleElemId ? MAX_IMAGE_HEIGHT : 'auto'}
              type={'number'}
              onChange={(value) => handleDimensionsChange('height', value)}
            />
          </Box>
        </Box>
        {!isDVEditor && (
          <Box className={styles.maintainProportionsContainer}>
            <Checkbox
              onChange={handleCheckChange}
              checked={maintainProportions}
              indeterminate={false}
              disableRipple={true}
              className={styles.checkbox}
            />
            <Typography.SuperSmallCaption className={styles.label}>
              {content.maintainProportions}
            </Typography.SuperSmallCaption>
          </Box>
        )}

        {!isImageEditor && (
          <>
            <Box className={`${styles.widthSection}`}>
              <Box className={styles.inputContainer}>
                <Typography.SuperSmallCaption className={styles.caption}>
                  {content.altText}
                </Typography.SuperSmallCaption>
                <TextField
                  size="small"
                  direction="vertical"
                  placeholder={textTemplate(content.enterValue, { value: content.altText.toLowerCase() })}
                  value={state?.altText && state.altText}
                  onChange={handleAltTextChange}
                />
              </Box>
            </Box>
          </>
        )}

        {isImageEditor && !isDVEditor && (
          <Box className={styles.saveCloseContainer}>
            <Button className={styles.closeBtn} variant="outlined" color="primary" onClick={onCloseImageEditor}>
              {content.close}
            </Button>

            <Button
              className={styles.save}
              variant="contained"
              color="secondary"
              size="medium"
              disabled={!dirty}
              onClick={dirty ? onSave : undefined}
            >
              {content.saveChanges}
            </Button>
          </Box>
        )}
      </>
    );
  };

  const handleTriggerPanelTitle = useCallback(() => {
    // Keep the edit name mode off for the image editor until rename functionality is sorted out.
    // Editing a name currently creates a duplicate image instead of just renaming the existing one.
    setIsEditTitleModeOn(false);

    // setIsEditTitleModeOn((prev) => !prev);
  }, []);

  const handlePanelTitleValueChange = useCallback(
    (e: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
      if (setPanelTitleValue) {
        setPanelTitleValue(e.target.value);
      }

      setIsModuleChanged(true);
    },
    [setIsModuleChanged, setPanelTitleValue],
  );

  const handleCancelRenaming = useCallback(() => {
    handleTriggerPanelTitle();
    setIsModuleChanged(false);

    if (setPanelTitleValue) {
      setPanelTitleValue(name);
    }
  }, [handleTriggerPanelTitle, setIsModuleChanged, setPanelTitleValue, name]);

  useEffect(() => {
    setState({
      chosenModuleElemId: chosenImage.chosenModuleElemId || '',
      imageSrc: chosenImage.imageSrc || '',
      width: chosenImage.width?.toString(),
      height: chosenImage.height?.toString(),
      link: chosenImage.link ?? '',
      title: chosenImage.title ?? '',
      altText: chosenImage.altText ?? '',
    });

    setActiveDimensionField('width');
    setMaintainProportions(true);
  }, [
    chosenImage,
    // width,
    // height,
    setActiveDimensionField,
    setState,
    setMaintainProportions,
  ]);

  // Prevent setState and onDimensionsChange on initial mount
  const didAutoAdjustMount = useRef(false);

  useEffect(() => {
    if (!didAutoAdjustMount.current) {
      didAutoAdjustMount.current = true;
      return;
    }
    if (maintainProportions && activeDimensionField === 'width') {
      setState((prev) => {
        return {
          ...prev,
          height: roundByRank(Number(state?.width) / imageRatio, 1).toString(),
        };
      });
    }
  }, [state?.width, activeDimensionField, maintainProportions, imageRatio]);

  useEffect(() => {
    if (!didAutoAdjustMount.current) {
      didAutoAdjustMount.current = true;
      return;
    }
    if (maintainProportions && activeDimensionField === 'height') {
      setState((prev) => {
        return {
          ...prev,
          width: roundByRank(Number(state?.height) * imageRatio, 1).toString(),
        };
      });
    }
  }, [state?.height, activeDimensionField, maintainProportions, imageRatio]);

  return (
    <>
      {!isDVEditor ? (
        <Box className={styles.imageContainer} data-ui-type={uiType}>
          {/* {isImageEditor ? (
            isEditTitleModeOn ? (
              <Box className={styles.renameBar}>
                <Typography.SuperSmallCaption className={styles.panelHeader}>
                  {content.imageName}
                </Typography.SuperSmallCaption>
                <InputBase
                  onDoubleClick={handleTriggerPanelTitle}
                  value={panelTitleValue}
                  onChange={handlePanelTitleValueChange}
                />
                <Box className={styles.renameBarButtons}>
                  <Button
                    className={styles.cancelBtn}
                    variant="outlined"
                    color="primary"
                    onClick={handleCancelRenaming}
                  >
                    {content.cancel}
                  </Button>
                </Box>
              </Box>
            ) : (
              <Box onDoubleClick={handleTriggerPanelTitle} className={styles.titleWrapper}>
                <Typography.SuperSmallCaption className={styles.panelHeader}>
                  {content.imageName}
                </Typography.SuperSmallCaption>
                <Typography.Title className={styles.title}>{name}</Typography.Title>
              </Box>
            )
          ) : (
            <Box className={styles.titleWrapper}>
              <Typography.Title className={styles.title} style={{ padding: '0' }}>
                {content.image}
              </Typography.Title>
            </Box>
          )} */}
          {isImageEditor ? (
            <Box className={styles.content}>
              {isEditTitleModeOn ? (
                <Box className={styles.renameBar}>
                  <Typography.SuperSmallCaption className={styles.panelHeader}>
                    {content.imageName}
                  </Typography.SuperSmallCaption>
                  <InputBase
                    onDoubleClick={handleTriggerPanelTitle}
                    value={panelTitleValue}
                    onChange={handlePanelTitleValueChange}
                  />
                  <Box className={styles.renameBarButtons}>
                    <Button
                      className={styles.cancelBtn}
                      variant="outlined"
                      color="primary"
                      onClick={handleCancelRenaming}
                    >
                      {content.cancel}
                    </Button>
                  </Box>
                </Box>
              ) : (
                <Box onDoubleClick={handleTriggerPanelTitle} className={styles.titleWrapper}>
                  <Typography.SuperSmallCaption className={styles.panelHeader}>
                    {content.imageName}
                  </Typography.SuperSmallCaption>
                  <Typography.Body className={styles.elementName}>{name}</Typography.Body>
                </Box>
              )}
              {getContent()}
            </Box>
          ) : (
            <Box className={styles.contentContainer}>
              <Box className={styles.settingsContainer}>
                <Accordion
                  expanded={isSectionExpanded[0]}
                  onChange={() => handleAccordionChange(0)}
                  title={content.settings}
                  uiType="quaternary"
                >
                  {getContent()}
                </Accordion>
                <Accordion
                  expanded={isSectionExpanded[1]}
                  onChange={() => handleAccordionChange(1)}
                  title={content.linkSettings}
                  uiType="quaternary"
                >
                  <Box className={styles.widthSection}>
                    <Box className={styles.inputContainer}>
                      <Typography.SuperSmallCaption className={styles.caption}>
                        {content.linkUrl}
                      </Typography.SuperSmallCaption>
                      <TextField
                        size="small"
                        direction="vertical"
                        placeholder={textTemplate(content.enterValue, { value: content.linkAddress.toLowerCase() })}
                        value={state?.link && state.link}
                        onChange={handleLinkChange}
                      />
                    </Box>
                  </Box>
                  <Box className={`${styles.widthSection} ${styles.bottomInput}`}>
                    <Box className={styles.inputContainer}>
                      <Typography.SuperSmallCaption className={styles.caption}>
                        {content.title}
                      </Typography.SuperSmallCaption>
                      <TextField
                        size="small"
                        direction="vertical"
                        placeholder={textTemplate(content.enterValue, { value: content.title.toLowerCase() })}
                        disabled={!state?.link || state.link.length === 0}
                        value={state?.title && state.title}
                        onChange={handleTitleChange}
                        className={styles.linkTitleInput}
                      />
                    </Box>
                  </Box>
                </Accordion>
                <Box className={styles.buttonContainer}>
                  <Button
                    className={styles.close}
                    color="primary"
                    variant="outlined"
                    size="medium"
                    onClick={onModuleClose}
                  >
                    {content.close}
                  </Button>
                  <Button
                    className={styles.save}
                    variant="contained"
                    color="secondary"
                    size="medium"
                    disabled={!isModuleChanged}
                    onClick={isModuleChanged ? onModuleSave : undefined}
                  >
                    {content.save}
                  </Button>
                </Box>
              </Box>
            </Box>
          )}
          {/* <Box className={styles.editModeContainer}>
            <EditMode />
          </Box> */}
        </Box>
      ) : (
        <Box className={styles.DVcontent}>{getContent()}</Box>
      )}
    </>
  );
};
